(() => {
  'use strict';
  const root = document.getElementById('ttQuiz');
  if (!root) return;
  const strings = JSON.parse(document.getElementById('ttQuizStrings').textContent);
  const results = root.querySelector('#ttResults');
  const state = { step: 'intro', answers: { activities: [] }, name: '' };
  const paths = {
    parent: ['intro','1','2','3','4','5','7','8','results'],
    gift: ['intro','1','2','3g','4g','7','8','results'],
    unsure: ['intro','1','2e','3e','7','8','results']
  };
  let catalogPromise = null;
  let transition = false;
  let resultsGeneration = 0;
  const text = (tag, value, className) => {
    const node = document.createElement(tag);
    node.textContent = value;
    if (className) node.className = className;
    return node;
  };
  function path() { return paths[state.answers.who] || paths.parent; }
  function remember() {
    try { sessionStorage.setItem('tt_quiz_catalog_v3', JSON.stringify({ step: state.step, answers: state.answers })); } catch (_) {}
  }
  function showStep(step, scroll = true) {
    state.step = step;
    root.querySelectorAll('.tt-step,.tt-results').forEach(node => node.classList.toggle('active', node.dataset.step === step));
    const order = path();
    const index = order.indexOf(step);
    const count = order.length - 2;
    const eyebrow = root.querySelector(`[data-step="${step}"] .tt-eyebrow`);
    if (eyebrow && step !== '8') eyebrow.textContent = `Question ${index}`;
    const progress = root.querySelector('#ttProgress');
    progress.style.display = step === 'intro' || step === 'results' ? 'none' : 'block';
    root.querySelector('#ttStepLabel').textContent = `Step ${index} of ${count}`;
    root.querySelector('#ttStepPct').textContent = Math.round(index / count * 100) + '%';
    root.querySelector('#ttProgressFill').style.width = Math.min(100,index / count * 100) + '%';
    root.querySelectorAll('[data-name-placeholder]').forEach(node => { node.textContent = state.name || 'your child'; });
    root.querySelectorAll('[data-q]').forEach(node => {
      const value = state.answers[node.dataset.q];
      const selected = Array.isArray(value) ? value.includes(node.dataset.value) : value === node.dataset.value;
      node.classList.toggle('selected', selected);
      node.setAttribute('aria-pressed', String(selected));
    });
    remember();
    if (scroll) root.scrollIntoView({ behavior: 'auto', block: 'start' });
    if (step === 'results') renderResults();
    else {
      const heading = root.querySelector(`[data-step="${step}"] :is(h1,h2)`);
      if (heading && scroll) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    }
    window.dataLayer?.push({ event: 'tt_quiz_step', step, path: state.answers.who || 'parent' });
  }
  function next() {
    const order = path();
    const current = order.indexOf(state.step);
    if (current >= 0 && current < order.length - 1) showStep(order[current + 1]);
  }
  async function loadCatalog() {
    const base = new URL(root.dataset.catalogUrl, location.origin);
    if (base.origin !== location.origin || !base.pathname.includes('/collections/')) throw new Error('Invalid catalog');
    let url = new URL(base);
    url.search = '';
    const items = new Map();
    let total = null;
    let collectionId = null;
    let complete = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      for (let page = 1; page <= 4; page += 1) {
        url.searchParams.set('view', 'gift-index');
        url.searchParams.set('section_id', 'tt-gift-catalog-data');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Catalog unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-gift-catalog-json]');
        if (!node) throw new Error('Missing catalog');
        const data = JSON.parse(node.textContent);
        if (data.version !== 1 || data.currency !== root.dataset.currency || data.country !== root.dataset.country || data.locale !== root.dataset.locale || Number(data.page) !== page || !Array.isArray(data.items)) throw new Error('Catalog context changed');
        if (page === 1) { total = Number(data.total); collectionId = String(data.collection_id); }
        if (!Number.isInteger(total) || total < 0 || total > 1000 || Number(data.total) !== total || String(data.collection_id) !== collectionId) throw new Error('Catalog changed');
        for (const item of data.items) {
          const productUrl = new URL(item.url, location.origin);
          if (!item.id || productUrl.origin !== location.origin || !productUrl.pathname.includes('/products/') || typeof item.title !== 'string' || typeof item.available !== 'boolean' || !Number.isFinite(item.price) || item.price < 0 || !Array.isArray(item.age_groups) || !Array.isArray(item.interests)) throw new Error('Invalid catalog product');
          items.set(String(item.id), item);
        }
        if (!data.next_url) { complete = true; break; }
        url = new URL(data.next_url, base);
        if (url.origin !== base.origin || url.pathname !== base.pathname) throw new Error('Invalid catalog page');
      }
      if (!complete || items.size !== total) throw new Error('Incomplete catalog');
      return [...items.values()];
    } finally { clearTimeout(timeout); }
  }
  function getCatalog() {
    if (!catalogPromise) catalogPromise = loadCatalog().catch(error => { catalogPromise = null; throw error; });
    return catalogPromise;
  }
  function ageGroups(answers) {
    const groups = {
      newborn: ['0-11-months'], baby: ['0-11-months'], early_toddler: ['1-year'], toddler: ['1-year','2-years'],
      older_toddler: ['2-years','3-years'], preschool: ['3-years','4-years'], kindergarten_prep: ['4-years','5-plus'],
      one_two: ['1-year','2-years'], two_four: ['2-years','3-years','4-years'], mixed: ['0-11-months','1-year','2-years','3-years','4-years','5-plus']
    };
    return groups[answers.age] || [];
  }
  function rankProducts(items, answers) {
    const ages = ageGroups(answers);
    const interests = new Set();
    const activityMap = { sensory: ['baby','hands'], fine_motor: ['hands'], language: ['school','together'], auditory: ['space','bedtime'], creative: ['making'], practical_life: ['everyday','hands'], problem_solving: ['school','hands'] };
    for (const activity of answers.activities || []) for (const interest of activityMap[activity] || []) interests.add(interest);
    if (answers.context === 'travel' || answers.pain === 'travel') interests.add('travel');
    if (answers.context === 'bath') interests.add('bath');
    if (answers.pain === 'development' || answers.gift_style === 'educational') interests.add('school');
    if (answers.pain === 'calm_focus') interests.add('bedtime');
    if (answers.occasion === 'holiday') interests.add('christmas');
    if (answers.occasion === 'baby_shower') interests.add('baby');
    return items.filter(item => item.available && item.image?.src && item.price > 0 && item.gift_type !== 'gift_card' && item.gift_type !== 'digital' && item.age_groups.some(age => ages.includes(age)))
      .map((item,index) => {
        let score = item.interests.filter(interest => interests.has(interest)).length * 10;
        if (item.gift_type === 'bundle') score += answers.tier === 'complete' ? 12 : answers.tier === 'essential' ? -8 : 3;
        if (item.compare_at > item.price) score += 2;
        return { item, score, index };
      }).sort((a,b) => b.score - a.score || a.index - b.index).map(entry => entry.item);
  }
  function link(label, href, className = 'tt-btn') {
    const node = text('a', label, className);
    node.href = href;
    return node;
  }
  function card(item) {
    const node = document.createElement('article');
    node.className = 'tt-quiz-card';
    const photoLink = link('', item.url, 'tt-quiz-photo-link');
    photoLink.setAttribute('aria-label', item.title);
    const photo = document.createElement('img');
    photo.src = item.image.src;
    photo.srcset = item.image.srcset || '';
    photo.sizes = '(min-width: 600px) 380px, calc(100vw - 80px)';
    photo.width = item.image.width || 640;
    photo.height = item.image.height || 640;
    photo.alt = item.image.alt || item.title;
    photo.loading = 'lazy';
    photoLink.append(photo);
    node.append(photoLink);
    if (item.compare_at > item.price) node.append(text('span', strings.save + ' ' + Math.round((1 - item.price / item.compare_at) * 100) + '%', 'tt-quiz-save'));
    node.append(text('h3', item.title));
    if (item.reason) node.append(text('p', item.reason));
    if (item.age_note) node.append(text('p', item.age_note, 'tt-quiz-age'));
    const price = document.createElement('div');
    price.className = 'tt-quiz-price';
    price.append(text('strong', (item.price_varies ? strings.from + ' ' : '') + item.price_text));
    if (item.compare_at > item.price) price.append(text('s', item.compare_at_text));
    node.append(price, link(strings.view_deal, item.url));
    return node;
  }
  async function renderResults() {
    const generation = ++resultsGeneration;
    results.replaceChildren(text('h2', strings.results, 'tt-results-headline'), text('p', strings.loading, 'tt-quiz-status'));
    results.setAttribute('aria-busy','true');
    try {
      const items = await getCatalog();
      if (generation !== resultsGeneration || state.step !== 'results') return;
      const picks = rankProducts(items,state.answers).slice(0,state.answers.tier === 'essential' ? 4 : state.answers.tier === 'complete' ? 8 : 6);
      const heading = text('h2', strings.results + (state.name ? ': ' + state.name : ''), 'tt-results-headline');
      heading.tabIndex = -1;
      results.replaceChildren(heading, text('p', strings.results_help, 'tt-results-sub'));
      if (picks.length) {
        const grid = document.createElement('div');
        grid.className = 'tt-quiz-grid';
        picks.forEach(item => grid.append(card(item)));
        results.append(grid, text('p',strings.catalog_note,'tt-quiz-catalog-note'),text('p',strings.age_note,'tt-quiz-catalog-note'));
      } else results.append(text('p', strings.empty, 'tt-quiz-status'));
      results.append(link(strings.browse,root.dataset.catalogUrl,'tt-btn tt-btn-secondary'));
      const restart = text('button', strings.restart, 'tt-back');
      restart.type = 'button';
      restart.addEventListener('click',() => { resultsGeneration += 1; state.answers = {activities:[]}; state.name = ''; root.querySelector('#ttChildName').value = ''; root.querySelector('#ttEmail').value = ''; showStep('intro'); });
      results.append(restart);
      heading.focus({preventScroll:true});
      window.dataLayer?.push({event:'tt_quiz_complete',recommended_products:picks.map(item=>item.id),answers:state.answers});
    } catch (_) {
      if (generation !== resultsGeneration || state.step !== 'results') return;
      const retry = text('button',strings.retry,'tt-btn');
      retry.type = 'button';
      retry.addEventListener('click',renderResults);
      results.replaceChildren(text('p',strings.load_error,'tt-quiz-status'),retry,link(strings.browse,root.dataset.catalogUrl,'tt-btn tt-btn-secondary'));
    } finally { if (generation === resultsGeneration) results.removeAttribute('aria-busy'); }
  }
  root.addEventListener('click',event => {
    const target = event.target.closest('[data-action],[data-q]');
    if (!target || !root.contains(target) || transition) return;
    const action = target.dataset.action;
    if (action === 'start') { getCatalog().catch(()=>{}); next(); }
    else if (action === 'back') { const order = path(); showStep(order[Math.max(0,order.indexOf(state.step)-1)]); }
    else if (action === 'next') next();
    else if (action === 'submit' || action === 'skip-email') {
      const emailInput = root.querySelector('#ttEmail');
      const email = emailInput.value.trim();
      if (action === 'submit' && email && !emailInput.checkValidity()) { emailInput.setAttribute('aria-invalid','true'); emailInput.reportValidity(); emailInput.focus(); return; }
      emailInput.removeAttribute('aria-invalid');
      state.name = root.querySelector('#ttChildName').value.trim().slice(0,60);
      if (action === 'submit' && email) window.dataLayer?.push({event:'tt_quiz_email_capture',email,child_name:state.name,quiz_age:state.answers.age,quiz_pain:state.answers.pain});
      next();
    } else if (target.dataset.q) {
      const key = target.dataset.q;
      const value = target.dataset.value;
      if (target.classList.contains('multi')) {
        const values = state.answers[key] || [];
        state.answers[key] = values.includes(value) ? values.filter(item=>item!==value) : [...values,value];
        target.classList.toggle('selected',state.answers[key].includes(value));
        target.setAttribute('aria-pressed',String(state.answers[key].includes(value)));
        remember();
      } else {
        if (key === 'who' && state.answers.who !== value) state.answers = {activities:[]};
        state.answers[key] = value;
        target.classList.add('selected');
        transition = true;
        setTimeout(()=>{transition=false;next();},180);
      }
    }
  });
  root.querySelector('#ttChildName').setAttribute('aria-label',root.querySelector('#ttChildName').placeholder);
  root.querySelector('#ttEmail').setAttribute('aria-label',root.querySelector('#ttEmail').placeholder);
  try {
    const saved = JSON.parse(sessionStorage.getItem('tt_quiz_catalog_v3'));
    if (saved?.answers && typeof saved.answers === 'object') {
      state.answers = saved.answers;
      if (path().includes(saved.step) && saved.step !== 'results') state.step = saved.step;
    }
  } catch (_) {}
  showStep(state.step,false);
})();
